import { useTheme, Theme } from "@mui/material/styles";
import { Check, LanguageOutlined } from "@mui/icons-material";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { useMicrospecDynamicUIContext } from "../GlobalProvider";
import { handleLanguageChange, useMicrospecThemeLanguageContext } from "../Themes";

interface LanguagesMenuProps {
    size?: "small" | "medium" | "large";
}

const LanguagesMenu: React.FC<LanguagesMenuProps> = ({ size = "large" }) => {
    const { config } = useMicrospecDynamicUIContext();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const theme = useTheme() as Theme;
    const { languages } = config;
    const currentLanguage = localizationProvider.choosen;

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLanguagePick = (event: React.MouseEvent<HTMLElement>) => {
        const language = event.currentTarget.id;
        handleLanguageChange(language);
    };

    return (
        <>
            <Tooltip title={localizationProvider.translateByType({
                type: "Internal",
                text: "languages"
            })}>
                <IconButton
                    id="languages"
                    size={size}
                    aria-label="languages"
                    color={theme.palette.mode === 'dark' ? 'inherit' : 'primary'}
                    sx={{
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main,
                            backgroundColor: `${theme.palette.action.hover}15`,
                            transform: 'scale(1.1)',
                            boxShadow: `0 4px 15px ${theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main}30`
                        }
                    }}
                    onClick={handleClick}
                >
                    <LanguageOutlined />
                </IconButton>
            </Tooltip>
            <Menu
                id="languages-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: 2,
                        boxShadow: theme.palette.mode === 'dark'
                            ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                            : '0 8px 32px rgba(0, 0, 0, 0.1)',
                        backdropFilter: 'blur(10px)',
                        background: theme.palette.mode === 'dark'
                            ? 'rgba(18, 18, 18, 0.95)'
                            : 'rgba(255, 255, 255, 0.95)',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`
                    }
                }}
            >
                {
                    languages.list.map((value) => {
                        const isSelected = value.code === currentLanguage;
                        return <MenuItem 
                            id={value.code} 
                            key={value.code} 
                            onClick={handleLanguagePick}
                            selected={isSelected}
                            sx={{
                                color: theme.palette.text.primary,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                                ...(isSelected && {
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.08)'
                                        : 'rgba(0, 0, 0, 0.04)',
                                    color: theme.palette.mode === 'dark' 
                                        ? theme.palette.primary.light 
                                        : theme.palette.primary.main,
                                    fontWeight: 600
                                }),
                                '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.08)'
                                        : 'rgba(0, 0, 0, 0.04)',
                                    color: theme.palette.mode === 'dark' 
                                        ? theme.palette.primary.light 
                                        : theme.palette.primary.main
                                }
                            }}
                        >
                            {value.name}
                            {isSelected && (
                                <Check 
                                    sx={{ 
                                        fontSize: '1.2rem',
                                        color: theme.palette.mode === 'dark' 
                                            ? theme.palette.primary.light 
                                            : theme.palette.primary.main
                                    }} 
                                />
                            )}
                        </MenuItem>;
                    })
                }
            </Menu>
        </>
    );
};

export default LanguagesMenu;